import { IApiArgument, IButtonAction, IDynamicForm } from "config";
import { BREAK_ACTIONS, IButtonActionOptions } from "containers/DynamicForm/useButtonAction";
import replaceWyshwareUri from "utils/replaceWyshwareUri";

import { useCheckWyshAllocations } from "./useCheckWyshAllocations";
import { useCreateAcknowledgements } from "./useCreateAcknowledgements";
import { useCreateResponse } from "./useCreateResponse";
import { useDismiss } from "./useDismiss";
import { useDivideEvenly } from "./useDivideEvenly";
import { useExecuteGroupApi } from "./useExecuteGroupApi";
import { useExitFlowWarning } from "./useExitFlowWarning";
import { useFinishFlow } from "./useFinishFlow";
import { useFormUnchangedSubmitWarning } from "./useFormUnchangedSubmitWarning";
import { useFormUnsavedDiffWarning } from "./useFormUnsavedDiffWarning";
import { useNextForm } from "./useNextForm";
import { usePop } from "./usePop";
import { usePopToRoot } from "./usePopToRoot";
import { useRefreshForm } from "./useRefreshForm";
import { useSetFormAction } from "./useSetFormAction";
import { useShowCloseAccountFailedModal } from "./useShowCloseAccountFailedModal";
import { useShowInsuranceApplication } from "./useShowInsuranceApplication";
import { useShowSuccessOverlay } from "./useShowSuccessOverlay";
import { useShowUnderwritingWaitTimeModal } from "./useShowUnderwritingWaitTimeModal";
import { useShowVerificationEmailSentModal } from "./useShowVerificationEmailSentModal";
import { useUpdateBeneficiaryAllocations } from "./useUpdateBeneficiaryAllocations";
import { useUploadDocumentsToCloud } from "./useUploadDocumentsToCloud";
import { useValidateForm } from "./useValidateForm";

export const useGetButtonCommand = () => {
  const checkWyshAllocationsCommand = useCheckWyshAllocations();
  const createAcknowledgementsCommand = useCreateAcknowledgements();
  const createResponseCommand = useCreateResponse();
  const dismissCommand = useDismiss();
  const divideEvenly = useDivideEvenly();
  const executeGroupApi = useExecuteGroupApi();
  const exitFlowWarning = useExitFlowWarning();
  const finishFlow = useFinishFlow();
  const formUnchangedSubmitWarningCommand = useFormUnchangedSubmitWarning();
  const formUnsavedDiffWarningCommand = useFormUnsavedDiffWarning();
  const nextFormCommand = useNextForm();
  const popCommand = usePop();
  const popToRootCommand = usePopToRoot();
  const refreshFormCommand = useRefreshForm();
  const setFormActionCommand = useSetFormAction();
  const showCloseAccountFailedCommand = useShowCloseAccountFailedModal();
  const showInsuranceApplication = useShowInsuranceApplication();
  const showSuccessOverlayCommand = useShowSuccessOverlay();
  const showUnderwritingWaitTimeModal = useShowUnderwritingWaitTimeModal();
  const showVerificationEmailSentModal = useShowVerificationEmailSentModal();
  const updateBeneficiaryAllocations = useUpdateBeneficiaryAllocations();
  const uploadDocumentsToCloudCommand = useUploadDocumentsToCloud();
  const validateFormCommand = useValidateForm();

  const getApiButtonCommand = (command: IButtonAction["command"]): TApiButtonCommandFn => {
    const commandDestination = command.destination as ButtonCommandDestinationTypes;

    switch (commandDestination) {
      case "checkWyshAllocations":
        return checkWyshAllocationsCommand;

      case "createAcknowledgements":
        return createAcknowledgementsCommand;

      case "createResponse":
        return createResponseCommand;

      case "dismiss":
        return dismissCommand;

      case "divideEvenly":
        return divideEvenly;

      case "executeGroupApi":
        return executeGroupApi;

      case "exitFlowWarning":
        return exitFlowWarning;

      case "finishFlow":
        return finishFlow;

      case "formUnchangedSubmitWarning":
        return formUnchangedSubmitWarningCommand;

      case "formUnsavedDiffWarning":
        return formUnsavedDiffWarningCommand;

      case "nextForm":
        return nextFormCommand;

      case "pop":
        return popCommand;

      case "popToRoot":
        return popToRootCommand;

      case "refreshForm":
        return refreshFormCommand;

      case "setFormAction":
        return setFormActionCommand;

      case "showCloseAccountFailedModal":
        return showCloseAccountFailedCommand;

      case "showInsuranceApplication":
        return showInsuranceApplication;

      case "showSuccessOverlay":
        return showSuccessOverlayCommand;

      case "showUnderwritingWaitTimeModal":
        return showUnderwritingWaitTimeModal;

      case "showVerificationEmailSentModal":
        return showVerificationEmailSentModal;

      case "updateBeneficiaryAllocations":
        return updateBeneficiaryAllocations;

      case "uploadDocumentsToCloud":
        return uploadDocumentsToCloudCommand;

      case "validateForm":
        return validateFormCommand;

      default:
        return () => false;
    }
  };

  const getLinkButtonCommand = (command: IButtonAction["command"]) => {
    return replaceWyshwareUri(command.destination) || "/";
  };

  return {
    getApiButtonCommand,
    getLinkButtonCommand,
  };
};

/* ------------------    TYPES    ------------------ */
type ButtonCommandDestinationTypes = keyof typeof EnumButtonCommandApiDestination;

const EnumButtonCommandApiDestination = {
  checkWyshAllocations: "checkWyshAllocations",
  createAcknowledgements: "createAcknowledgements",
  createResponse: "createResponse",
  dismiss: "dismiss",
  divideEvenly: "divideEvenly",
  executeGroupApi: "executeGroupApi",
  exitFlowWarning: "exitFlowWarning",
  finishFlow: "finishFlow",
  formUnchangedSubmitWarning: "formUnchangedSubmitWarning",
  formUnsavedDiffWarning: "formUnsavedDiffWarning",
  nextForm: "nextForm",
  pop: "pop",
  popToRoot: "popToRoot",
  popToDashboard: "popToDashboard",
  refreshForm: "refreshForm",
  setFormAction: "setFormAction",
  showCloseAccountFailedModal: "showCloseAccountFailedModal",
  showInsuranceApplication: "showInsuranceApplication",
  showSuccessOverlay: "showSuccessOverlay",
  showUnderwritingWaitTimeModal: "showUnderwritingWaitTimeModal",
  showVerificationEmailSentModal: "showVerificationEmailSentModal",
  updateBeneficiaryAllocations: "updateBeneficiaryAllocations",
  uploadDocumentsToCloud: "uploadDocumentsToCloud",
  validateForm: "validateForm",
} as const;

export type TApiButtonCommandFnArgs = IButtonActionOptions;

export type TApiButtonCommandFn = (
  commandArgs: IApiArgument[],
  buttonActionArgs: TApiButtonCommandFnArgs
) =>
  | boolean
  | string
  | void
  | Promise<unknown[] | IDynamicForm | string | void | { formAction: string }>
  | typeof BREAK_ACTIONS;
